import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FC, useEffect } from 'react';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import {
	useHandwrittenCardAllCategoryTemplates,
	useHandwrittenCardCategories,
	useHandwrittenCardCategoryTemplates,
	useHandwrittenCardSearchTemplates,
	useMyTemplatesQuery,
} from '../../../queries';
import { IHandwrittenCardTemplate, isAdmin, KnownCategories, TemplateType } from '../../../viewmodels/AppViewModels';
import { Button } from '../../components-v2/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { PurchaseCreditsModal } from '../../components/settings/PurchaseCreditsModal';
import { OopsNotFoundGraphic } from '../../components/svgs/graphics/OopsNotFoundGraphic';
import { ArrowIcon } from '../../components/svgs/icons/ArrowIcon';
import { EmailIcon } from '../../components/svgs/icons/EmailIcon';
import { HandwrittenCardsTemplateCard } from '../../components/templateCards/HandwrittenCardsTemplateCard';
import { brandSecondary } from '../../styles/colors';
import { baseStyleSheet, bs } from '../../styles/styles';
import { CampaignBrowser, useCampaignParams, useEnforceIndustryAndCategory } from '../CampaignBrowser';
import { PostcardComposerStep } from '../PostcardComposer';
import { useCurrentUserGiftingBalance } from '../PostcardComposer/hooks/useCurrentUserGiftingBalance';
import {
	ContentLibraryContent,
	ContentLibrarySection,
	ContentLibrarySectionHeader,
	ContentLibrarySectionHeaderTitle,
} from './presentation';
import { styleSheet } from './styles';

export const HandwrittenCardsCampaignCards: FC<{ setTabListAccessory?: (tabListAccessory: React.ReactNode) => void }> =
	observer(({ setTabListAccessory }) => {
		const userSession = useUserSession();
		const { category, industry, search, setCampaignBrowserParam } = useCampaignParams();
		const fullscreenModal = useFullscreenModal();
		useEnforceIndustryAndCategory({ setCampaignBrowserParam, tabUrlParam: 'cards' });
		const searchTemplatesQuery = useHandwrittenCardSearchTemplates({
			enabled: !!search,
			industry,
			search,
		});
		const categoryQuery = useHandwrittenCardCategories({
			enabled: !!industry,
			industry,
			withAll: true,
			withMyTemplates: true,
		});
		const categoryTemplatesQuery = useHandwrittenCardCategoryTemplates({
			category,
			enabled: category !== KnownCategories.All && category !== KnownCategories.MyTemplates,
			industry,
		});
		const myTemplatesQuery = useMyTemplatesQuery<IHandwrittenCardTemplate>({
			enabled: category === KnownCategories.MyTemplates,
			industry,
			templateType: TemplateType.HandwrittenCard,
		});
		const allCategoryTemplatesQuery = useHandwrittenCardAllCategoryTemplates({
			enabled: category === KnownCategories.All,
			industry,
		});
		const isSearching = !!search;
		const { cardsAvailable, currentBalanceFormatted, isLoading: isLoadingBalance } = useCurrentUserGiftingBalance();
		const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = React.useState(false);
		// We have data when we are searching and searchTemplatesQuery has data or
		// we are not searching and either allCategoryTemplatesQuery or categoryTemplatesQuery has data depending on whether the category is set to All
		const hasData = React.useMemo(() => {
			return (
				(isSearching && searchTemplatesQuery.data && searchTemplatesQuery.data.length > 0) ||
				(!isSearching &&
					((category === KnownCategories.All &&
						allCategoryTemplatesQuery.data &&
						Object.values(allCategoryTemplatesQuery.data).flat().length > 0) ||
						(category !== KnownCategories.All &&
							category !== KnownCategories.MyTemplates &&
							categoryTemplatesQuery.data &&
							categoryTemplatesQuery.data.length > 0) ||
						(category === KnownCategories.MyTemplates && myTemplatesQuery.data && myTemplatesQuery.data.length > 0)))
			);
		}, [
			isSearching,
			searchTemplatesQuery.data,
			allCategoryTemplatesQuery.data,
			categoryTemplatesQuery.data,
			myTemplatesQuery.data,
			category,
		]);
		// isLoading is true when we are searching and searchTemplatesQuery is loading or
		// we are not searching and either allCategoryTemplatesQuery or categoryTemplatesQuery is loading depending on whether the category is set to All
		const isLoading =
			(isSearching && searchTemplatesQuery.isPending) ||
			(!isSearching &&
				((category === KnownCategories.All && allCategoryTemplatesQuery.isPending) ||
					(category !== KnownCategories.All &&
						category !== KnownCategories.MyTemplates &&
						categoryTemplatesQuery.isPending) ||
					(category === KnownCategories.MyTemplates && myTemplatesQuery.isPending)));

		const handleChangeIndustry = (value: string) => {
			setCampaignBrowserParam({ industry: value, category: KnownCategories.All });
		};
		const handleChangeCategory = (value: string) => {
			setCampaignBrowserParam({ category: value === KnownCategories.Me ? KnownCategories.MyTemplates : value });
		};
		const handleSearchChange = React.useCallback(
			(value: string) => {
				setCampaignBrowserParam({ search: value });
			},
			[setCampaignBrowserParam]
		);
		const handleTemplateClick = (id: string) => {
			fullscreenModal.history.push(`/postcard/${id}/${PostcardComposerStep.Preview}`);
		};

		useEffect(() => {
			setTabListAccessory(
				isLoadingBalance ? (
					<LoadingSpinner type='extra-small' />
				) : (
					<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.hFull)}>
						<EmailIcon fillColor={brandSecondary} />
						{cardsAvailable != null ? (
							<span className={css(styleSheet.cardsAvailable)}>{cardsAvailable} Cards Available</span>
						) : null}
						{currentBalanceFormatted != null ? (
							<span className={css(styleSheet.balanceAvailable)}> = {currentBalanceFormatted}</span>
						) : null}
					</div>
				)
			);
			return () => {
				setTabListAccessory(null);
			};
		}, [isLoadingBalance, cardsAvailable, currentBalanceFormatted, setTabListAccessory]);

		return (
			<CampaignBrowser
				categories={categoryQuery.data ?? []}
				category={category}
				industry={industry}
				onCategoryChange={handleChangeCategory}
				onIndustryChange={handleChangeIndustry}
				onSearchChange={handleSearchChange}
				renderRightActions={
					<>
						{isAdmin(userSession.user) ? (
							<button
								className={css(baseStyleSheet.ctaButtonSecondary, styleSheet.addCreditBtn)}
								onClick={() => setShowPurchaseCreditsModal(true)}
							>
								+ Add Credits
							</button>
						) : null}
					</>
				} // To be replaced with renderNewCardButton when custom card is supported
				search={search}
				searchPlaceholder='Search card type or keywords'
			>
				{isSearching && searchTemplatesQuery.data && searchTemplatesQuery.data.length > 0 ? (
					<ContentLibraryContent>
						<ContentLibrarySection>
							<div className={css(styleSheet.cardSectionRow)}>
								{searchTemplatesQuery.data.map(template => (
									<HandwrittenCardsTemplateCard
										key={template.id}
										name={template.name}
										onTemplateClicked={() => handleTemplateClick(template.id)}
										publicUrl={template.thumbnail.publicUrl}
									/>
								))}
							</div>
						</ContentLibrarySection>
					</ContentLibraryContent>
				) : null}
				{!isSearching &&
				category !== KnownCategories.All &&
				category !== KnownCategories.MyTemplates &&
				categoryTemplatesQuery.data ? (
					<ContentLibraryContent>
						<ContentLibrarySection>
							<ContentLibrarySectionHeader>
								<ContentLibrarySectionHeaderTitle>{category}</ContentLibrarySectionHeaderTitle>
							</ContentLibrarySectionHeader>
							<div className={css(styleSheet.cardSectionRow)}>
								{categoryTemplatesQuery.data.map(template => (
									<HandwrittenCardsTemplateCard
										key={template.id}
										name={template.name}
										onTemplateClicked={() => handleTemplateClick(template.id)}
										publicUrl={template.thumbnail.publicUrl}
									/>
								))}
							</div>
						</ContentLibrarySection>
					</ContentLibraryContent>
				) : null}
				{!isSearching && category === KnownCategories.MyTemplates && myTemplatesQuery.data ? (
					<ContentLibraryContent>
						<ContentLibrarySection>
							<ContentLibrarySectionHeader>
								<ContentLibrarySectionHeaderTitle>{category}</ContentLibrarySectionHeaderTitle>
							</ContentLibrarySectionHeader>
							<div className={css(styleSheet.cardSectionRow)}>
								{myTemplatesQuery.data.map(template => (
									<HandwrittenCardsTemplateCard
										key={template.id}
										name={template.name}
										onTemplateClicked={() => handleTemplateClick(template.id)}
										publicUrl={template.thumbnail.publicUrl}
									/>
								))}
							</div>
						</ContentLibrarySection>
					</ContentLibraryContent>
				) : null}
				{!isSearching &&
				category === KnownCategories.All &&
				allCategoryTemplatesQuery.data &&
				Object.keys(allCategoryTemplatesQuery.data).length > 0 ? (
					<ContentLibraryContent>
						{Object.keys(allCategoryTemplatesQuery.data).map((categoryName: string) => {
							const templates = allCategoryTemplatesQuery.data[categoryName];
							return (
								<ContentLibrarySection key={categoryName}>
									<ContentLibrarySectionHeader>
										<ContentLibrarySectionHeaderTitle>
											{categoryName === KnownCategories.Me ? KnownCategories.MyTemplates : categoryName}
										</ContentLibrarySectionHeaderTitle>
										<Button variant='link' size='sm' onClick={() => handleChangeCategory(categoryName)}>
											Browse {categoryName === KnownCategories.Me ? KnownCategories.MyTemplates : categoryName}{' '}
											<ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
										</Button>
									</ContentLibrarySectionHeader>
									<div className={css(styleSheet.cardSectionRow)}>
										{templates.map(template => (
											<HandwrittenCardsTemplateCard
												key={template.id}
												name={template.name}
												onTemplateClicked={() => handleTemplateClick(template.id)}
												publicUrl={template.thumbnail.publicUrl}
											/>
										))}
									</div>
								</ContentLibrarySection>
							);
						})}
					</ContentLibraryContent>
				) : null}
				{isLoading && <LoadingSpinner className={css(styleSheet.pageLoading)} />}
				{!isLoading && !hasData ? (
					<div className={css(styleSheet.cardListEmpty)}>
						<OopsNotFoundGraphic />
						<p>We did not find any handwritten cards</p>
					</div>
				) : null}
				<PurchaseCreditsModal
					isOpen={showPurchaseCreditsModal}
					onRequestClose={() => setShowPurchaseCreditsModal(false)}
				/>
			</CampaignBrowser>
		);
	});
